import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProduct, postReview } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { Heart, Star, Truck, RotateCcw, ShieldCheck, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToCart, toggleWishlist, inWishlist, addRecent, recentlyViewed } = useStore();
  const [data, setData] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [color, setColor] = useState(null);
  const [size, setSize] = useState(null);
  const [zoom, setZoom] = useState(false);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    setData(null);
    getProduct(slug).then((d) => {
      setData(d); setColor(d.product.colors?.[0]?.name); setActiveImg(0); setReviews(d.reviews);
      addRecent(d.product);
    });
  }, [slug]);

  if (!data) return <div className="min-h-screen flex items-center justify-center font-serif text-2xl">Loading…</div>;
  const p = data.product;
  const saved = inWishlist(p.id);

  const handleAdd = () => {
    if (!size) { toast.error("Please select a size"); return; }
    addToCart(p, color, size);
  };

  return (
    <div data-testid="product-page">
      <div className="container-luxe py-8 text-xs text-muted-foreground font-display uppercase tracking-wide2">
        <Link to="/shop" className="link-underline">Shop</Link> / <Link to={`/shop?collection=${encodeURIComponent(p.collection)}`} className="link-underline">{p.collection}</Link> / <span className="text-charcoal">{p.name}</span>
      </div>

      <div className="container-luxe grid grid-cols-1 lg:grid-cols-2 gap-12 pb-20">
        {/* Gallery */}
        <div className="grid grid-cols-1 gap-3">
          <div className={`relative aspect-[4/5] overflow-hidden bg-beige/40 ${zoom ? "cursor-zoom-out" : "cursor-zoom-in"}`}
            onClick={() => setZoom(!zoom)} data-testid="product-main-image">
            <img src={p.images[activeImg]} alt={p.name} data-testid="product-main-image-img" className={`h-full w-full object-cover transition-transform duration-500 ${zoom ? "scale-150" : "scale-100"}`} />
          </div>
          <div className="grid grid-cols-4 gap-3">
            {p.images.map((img, i) => (
              <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                className={`aspect-square overflow-hidden bg-beige/40 ${activeImg === i ? "ring-1 ring-charcoal" : ""}`}>
                <img src={img} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Info — sticky */}
        <div className="lg:sticky lg:top-36 lg:h-fit">
          <p className="overline text-gold mb-3">{p.collection}</p>
          <h1 className="h-display text-4xl sm:text-5xl mb-4">{p.name}</h1>
          <div className="flex items-center gap-4 mb-6">
            <span className="font-display text-xl">${p.price}</span>
            {p.compare_at && <span className="text-muted-foreground line-through">${p.compare_at}</span>}
            {!p.demo && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Star size={13} className="fill-gold text-gold" /> {p.rating} ({reviews.length})</span>}
          </div>
          <p className="text-sm text-muted-foreground font-light leading-relaxed mb-8">{p.description}</p>

          {p.demo && (
            <div className="mb-8 border border-gold/40 bg-gold/5 px-4 py-3" data-testid="demo-notice">
              <p className="overline text-[10px] text-gold mb-1">Concept Product · Demo</p>
              <p className="text-xs text-muted-foreground font-light leading-relaxed">Preview only — this is a client concept. Inventory, checkout, reviews, rewards, and shipping are not live for this piece.</p>
            </div>
          )}

          <div className="mb-6">
            <p className="overline text-[10px] mb-3">Color — <span className="text-muted-foreground">{color}</span></p>
            <div className="flex gap-3">
              {p.colors.map((c) => (
                <button key={c.name} onClick={() => setColor(c.name)} title={c.name} data-testid={`color-${c.name}`}
                  className={`h-8 w-8 rounded-full border transition-all ${color === c.name ? "ring-2 ring-offset-2 ring-charcoal" : "border-black/10"}`} style={{ background: c.hex }} />
              ))}
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <p className="overline text-[10px]">Size</p>
              <button className="overline text-[10px] text-gold link-underline" data-testid="size-guide-btn"
                onClick={() => toast("Size Guide", { description: "XS 0-2 · S 4-6 · M 8-10 · L 12-14 · XL 16-18" })}>Size Guide</button>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {p.sizes.map((s) => (
                <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                  className={`py-3 text-sm font-display border transition-colors ${size === s ? "bg-charcoal text-bone border-charcoal" : "border-hairline hover:border-charcoal"}`}>{s}</button>
              ))}
            </div>
            {p.demo && <p className="mt-3 text-[11px] text-muted-foreground font-light" data-testid="size-note">Sizes XS–XL shown for preview — subject to supplier confirmation.</p>}
          </div>

          <div className="flex gap-3 mb-6">
            {p.demo ? (
              <button disabled className="btn-primary flex-1 opacity-60 cursor-not-allowed hover:bg-charcoal hover:text-bone" data-testid="concept-preview-btn">Concept Preview</button>
            ) : (
              <button onClick={handleAdd} className="btn-primary flex-1" data-testid="add-to-bag-btn">Add To Bag — ${p.price}</button>
            )}
            <button onClick={() => toggleWishlist(p)} data-testid="pdp-wishlist" aria-label="Wishlist"
              className="border border-charcoal px-5 flex items-center justify-center hover:bg-charcoal hover:text-bone transition-colors">
              <Heart size={18} strokeWidth={1.4} className={saved ? "fill-gold text-gold" : ""} />
            </button>
          </div>

          {!p.demo && (
            <div className="grid grid-cols-3 gap-4 py-6 border-y border-hairline text-center">
              {[[Truck, "Free Shipping $150+"], [RotateCcw, "30-Day Returns"], [ShieldCheck, "Secure Checkout"]].map(([Icon, label], i) => (
                <div key={i} className="flex flex-col items-center gap-2">
                  <Icon size={20} strokeWidth={1.2} className="text-gold" />
                  <span className="text-[10px] font-display uppercase tracking-wide2 text-muted-foreground">{label}</span>
                </div>
              ))}
            </div>
          )}

          <Accordion type="single" collapsible className="mt-4">
            <AccordionItem value="material" className="border-hairline">
              <AccordionTrigger className="overline text-[11px]">Material & Fit</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground font-light">{p.material}. Performance level: {p.performance}. True to size with a sculpting compression fit.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="care" className="border-hairline">
              <AccordionTrigger className="overline text-[11px]">Care Instructions</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground font-light">{p.care}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="ship" className="border-hairline">
              <AccordionTrigger className="overline text-[11px]">Shipping & Returns</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground font-light">{p.shipping}<br /><br />{p.returns}</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* Frequently bought together */}
      {data.frequently_bought?.length > 0 && (
        <section className="bg-beige/40 py-20" data-testid="frequently-bought">
          <div className="container-luxe">
            <Reveal><h2 className="h-display text-3xl sm:text-4xl mb-3">Complete The Look</h2><p className="text-sm text-muted-foreground font-light mb-10">Styled together by our team.</p></Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
              {data.frequently_bought.map((fp) => <ProductCard key={fp.id} product={fp} />)}
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      {!p.demo && (
      <section className="container-luxe py-20" data-testid="reviews-section">
        <Reveal><h2 className="h-display text-3xl sm:text-4xl mb-10">Customer Reviews</h2></Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {reviews.map((r) => (
            <div key={r.id} className="border-t border-hairline pt-6" data-testid={`review-${r.id}`}>
              <div className="flex gap-1 mb-3 text-gold">{[...Array(r.rating)].map((_, i) => <Star key={i} size={13} className="fill-gold" />)}</div>
              <h4 className="font-serif text-xl mb-2">{r.title}</h4>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-4">{r.body}</p>
              <p className="overline text-[10px]">{r.author} {r.verified && <span className="text-gold">· Verified</span>}</p>
            </div>
          ))}
        </div>
        <ReviewForm productId={p.id} onAdded={(rv) => setReviews((prev) => [rv, ...prev])} demo={p.demo} />
      </section>
      )}

      {/* Related */}
      {data.related?.length > 0 && (
        <section className="container-luxe py-16" data-testid="related-products">
          <Reveal><h2 className="h-display text-3xl sm:text-4xl mb-10">You May Also Like</h2></Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
            {data.related.map((rp) => <ProductCard key={rp.id} product={rp} />)}
          </div>
        </section>
      )}

      {/* Recently viewed */}
      {recentlyViewed.filter((r) => r.slug !== slug).length > 0 && (
        <section className="container-luxe pb-20" data-testid="recently-viewed">
          <h2 className="overline text-[11px] mb-8">Recently Viewed</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
            {recentlyViewed.filter((r) => r.slug !== slug).map((r) => (
              <Link key={r.id} to={`/product/${r.slug}`} className="group block" data-testid={`recent-${r.slug}`}>
                <div className="aspect-[4/5] overflow-hidden bg-[#EDE6DA]"><img src={r.image} alt={r.name} loading="lazy" className="h-full w-full object-contain object-bottom img-zoom" /></div>
                <div className="mt-4 flex items-start justify-between gap-3">
                  <h3 className="font-serif text-xl leading-tight">{r.name}</h3>
                  <span className="font-display text-sm">${r.price}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ReviewForm({ productId, onAdded, demo }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ rating: 5, title: "", body: "", author: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const rv = await postReview({ product_id: productId, ...form });
      onAdded(rv); setOpen(false); setForm({ rating: 5, title: "", body: "", author: "" });
      toast.success("Thank you for your review");
    } catch { toast.error("Could not submit review"); }
  };
  if (demo) return null;
  return (
    <div className="mt-12">
      {!open ? (
        <button onClick={() => setOpen(true)} className="btn-secondary" data-testid="write-review-btn"><Plus size={14} /> Write A Review</button>
      ) : (
        <form onSubmit={submit} className="max-w-lg border border-hairline p-8 space-y-4" data-testid="review-form">
          <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => (
            <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })}><Star size={22} className={n <= form.rating ? "fill-gold text-gold" : "text-hairline"} /></button>
          ))}</div>
          <input required placeholder="Your name" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="review-author" className="w-full border-b border-hairline py-2 bg-transparent outline-none text-sm" />
          <input required placeholder="Review title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="review-title" className="w-full border-b border-hairline py-2 bg-transparent outline-none text-sm" />
          <textarea required placeholder="Share your experience" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="review-body" rows={3} className="w-full border border-hairline p-3 bg-transparent outline-none text-sm" />
          <div className="flex gap-3"><button type="submit" className="btn-primary" data-testid="review-submit">Submit</button><button type="button" onClick={() => setOpen(false)} className="overline text-[10px]">Cancel</button></div>
        </form>
      )}
    </div>
  );
}
